// Share Types for Asset Share Feature

// Branding configuration for shares
export type ShareBranding = {
  logo_source?: 'watermark' | 'asset';  // Logo source type
  logo_asset_id?: string | null;         // Asset ID for logo (when logo_source is 'asset')
  logo_url?: string;                     // Ready-to-use URL from API response
  primary_color?: string;
  background_color?: string;
  use_watermark?: boolean;
};

// Static image size for download options
export type StaticImageSize = {
  width: number;
  height: number;
  format: 'jpg' | 'png' | 'webp' | 'gif';
  label?: string;
};

// Download options configuration
export type ShareDownloadOptions = {
  include_original?: boolean;
  image?: {
    odi?: {
      enabled: boolean;
      max_dimension?: number;
      formats?: string[]; // 'jpg', 'png', 'webp', 'gif'
    };
    odi_wm?: {  // Watermarked ODI (Custom Size Downloads with watermark)
      enabled: boolean;
      max_dimension?: number;
      formats?: string[]; // 'jpg', 'png', 'webp', 'gif'
      placement?: 'left-top' | 'right-top' | 'left-bottom' | 'right-bottom' | 'center' | 'repeat';
      scale?: number;    // width_scale percentage, default 20
      opacity?: number;  // 0 (opaque) to 100 (transparent), default 40
    };
    // Print-ready (CMYK) download. Serves the named print_export conversion
    // (default "print_ready"). Only appears in a viewer's downloads when the
    // conversion already exists on the asset.
    print_ready?: {
      enabled: boolean;
      conversion_name?: string;
    };
    static?: Array<string | StaticImageSize>;
  };
  video?: string[];
  document?: string[];
} | null;

// Preview options configuration
// Controls what viewers can preview before downloading
// Defaults: odi=false, odi_wm=true, video=['480p-wm-video'], document=[]
export type SharePreviewOptions = {
  image?: {
    odi?: { enabled: boolean };      // Image preview without watermark
    odi_wm?: { enabled: boolean };   // Image preview with watermark
  };
  video?: string[];   // Video transcode keys for preview (e.g., ['480p-wm-video'])
  document?: string[]; // Document preview options
};

// Access control configuration (for request)
export type ShareAccessControlRequest = {
  expiry?: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' | 'MAX' | string; // string for custom date, MAX = never expires
  password?: string | null;
  description?: string;
};

// Access control in response (expiry is Unix timestamp)
export type ShareAccessControlResponse = {
  expiry?: number; // Unix timestamp
  password?: string;
  description?: string;
};

// Share status types
export type ShareStatus = 'OK' | 'ACTIVE' | 'EXPIRED' | 'DISABLED';

// ODI (On-Demand Image) configuration for thumbnails
export type ShareAssetODI = {
  base_url: string;           // Public base URL for generating thumbnails on-demand
  allowed_formats: string[];  // Supported output formats (jpg, png, etc.)
  max_dimension: number;      // Maximum allowed dimension for generated images
};

// Conversion entry with display and download URLs
export type ShareAssetConversion = {
  key: string;                // e.g., "original", "480p-video", "720p-wm-video"
  label: string;              // Human-readable label e.g., "Original", "HD (720p)"
  display_url: string;        // Signed URL for viewing/streaming
  download_url: string;       // Signed URL for downloading
};

// Shared links structure for assets
export type ShareAssetSharedLinks = {
  odi: ShareAssetODI;                    // On-Demand Image URLs for thumbnails (all asset types)
  conversions: ShareAssetConversion[];   // Array of available conversions
};

// Share asset in response
export type ShareAsset = {
  id: string;
  status?: string;
  name: string;
  size?: number;
  content_type: string;
  width?: number;
  height?: number;
  duration?: number;
  shared_links: ShareAssetSharedLinks;
  approval_status?: string | null;
};

// Share params (nested in response)
export type ShareParams = {
  file_ids: string[];
  title: string;
  description?: string;
  allow_feedback?: boolean;
  allow_approval?: boolean;
  branding?: ShareBranding;
  access_control?: ShareAccessControlResponse;
};

// Main Share type (matches actual API response)
export type Share = {
  id: string;
  status: ShareStatus;
  title: string;
  description?: string; // Top-level from GET share response
  params?: ShareParams;
  branding?: ShareBranding;
  access_control?: ShareAccessControlResponse; // Top-level from GET share response
  download_options?: ShareDownloadOptions;
  preview_options?: SharePreviewOptions;
  allow_feedback: boolean;
  allow_approval: boolean;
  /** Per-share approval tallies, present on LIST responses. Zero on a share
   *  with approval disabled — that is a distribution link, not a review. */
  approved_count?: number;
  rejected_count?: number;
  pending_count?: number;
  /** When the owner needs a verdict back. NOT `expiry` — that is when the
   *  link dies and the reviewer gets a 410; this is advisory and routinely
   *  missed. Null on a distribution share, which has nothing to be late for. */
  due_on?: string | null;
  assets?: ShareAsset[];
  // For list endpoint - may have these at top level
  url?: string;
  expiry?: number; // Unix timestamp (legacy)
  expiry_utc?: string; // ISO date string e.g., "2026-02-08T00:00:00Z"
  asset_count?: number; // Direct count from list endpoint
  created_at?: number | string; // Unix timestamp or ISO date string
  user_id?: number;
  user_email?: string;
  shared_with?: string[]; // Owner-only: recipient email addresses
  // Provenance for the dashboard purpose badge. preset_id is the stable catalog
  // id the share's purpose maps to; template_id is the user template used, if any.
  preset_id?: string;
  template_id?: string;
};

// Helper to get title from share (handles nested structure)
export function getShareTitle(share: Share): string {
  return share.title || share.params?.title || '-';
}

// Helper to get expiry from share (handles nested structure and multiple formats)
// Returns Unix timestamp in seconds, or undefined if no expiry
export function getShareExpiry(share: Share): number | undefined {
  // First check for Unix timestamp at top level
  if (share.expiry && typeof share.expiry === 'number') {
    return share.expiry;
  }
  
  // Check for ISO date string at top level (expiry_utc)
  if (share.expiry_utc) {
    const timestamp = Date.parse(share.expiry_utc);
    if (!isNaN(timestamp)) {
      return Math.floor(timestamp / 1000); // Convert ms to seconds
    }
  }
  
  // Check top-level access_control (GET share response)
  if (share.access_control?.expiry) {
    return share.access_control.expiry;
  }
  
  // Check nested in params.access_control
  if (share.params?.access_control?.expiry) {
    return share.params.access_control.expiry;
  }
  
  return undefined;
}

// Helper to get file IDs from share
export function getShareFileIds(share: Share): string[] {
  return share.params?.file_ids || share.assets?.map(a => a.id) || [];
}

// Helper to get asset count from share
export function getShareAssetCount(share: Share): number {
  // Use direct asset_count if available (from list endpoint)
  if (typeof share.asset_count === 'number') {
    return share.asset_count;
  }
  // Fallback to counting assets array or file_ids
  if (share.assets?.length) {
    return share.assets.length;
  }
  return share.params?.file_ids?.length || 0;
}

// Helper to get description from share
// API returns description at top level only
export function getShareDescription(share: Share): string | undefined {
  return share.description || share.params?.description;
}

// API Response types
export type CreateShareResponse = {
  id: string;
  url: string;
  status: 'OK';
  expiry_utc?: string;
  expiry?: number;
};

export type ShareResponse = {
  status: 'OK' | 'ERROR';
  data: Share;
};

export type SharesListResponse = {
  status: 'OK' | 'ERROR';
  data: {
    shares: Share[];
    total_shares: number;
    // Backwards compatibility
    shares_found?: number;
  };
};

// Request types
export type CreateShareRequest = {
  file_ids: string[];
  title: string;
  description?: string;
  allow_feedback?: boolean;
  allow_approval?: boolean;
  branding?: ShareBranding;
  download_options?: ShareDownloadOptions;
  preview_options?: SharePreviewOptions;
  access_control?: ShareAccessControlRequest;
  template_id?: string;
  preset_id?: string; // Catalog preset the share's purpose maps to (purpose badge)
  email_to?: string[]; // Email addresses to share with
  /** ISO date or timestamp. When a verdict is wanted back — distinct from
   *  access_control.expiry, which is when the link stops working. */
  due_on?: string;
};

export type UpdateShareRequest = {
  /** Retire a review that will never come back — the external counterpart to
   *  withdrawing an assigned task. Sets the share's status to CLOSED. */
  close?: boolean;
  /** Set or move the review deadline. Moving it re-arms both reminders. */
  due_on?: string | null;
  title?: string;
  description?: string;
  password?: string | null;
  expiry?: string;
  branding?: ShareBranding;
  download_options?: ShareDownloadOptions;
  preview_options?: SharePreviewOptions;
  allow_feedback?: boolean;
  allow_approval?: boolean;
  file_ids?: string[]; // For adding assets to existing share
  add_emails?: string[]; // New recipients to notify
};

export type SharesListParams = {
  keyword?: string;
  start_date?: string;
  end_date?: string;
  shared_with?: string;
  status?: ShareStatus | string;
  approval_summary?: 'has_pending' | 'all_approved' | 'has_rejected' | string;
  sort_by?: string[];
  offset?: number;
  limit?: number;
};

// Comment types
export type ShareComment = {
  id: number;
  share_id: string;
  asset_id?: string;
  commenter_name: string;
  commenter_email: string;
  comment: string;
  created_utc: string;
};

export type ShareCommentsResponse = {
  status: 'OK' | 'ERROR';
  data: {
    comments: ShareComment[];
  };
};

// Approval types
export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export type ShareApproval = {
  asset_id: string;
  status: ApprovalStatus;
  reviewer_name?: string;
  reviewer_email?: string;
  notes?: string;
  updated_utc?: string;
};

export type ShareApprovalsResponse = {
  status: 'OK' | 'ERROR';
  data: {
    approvals: ShareApproval[];
  };
};

// Analytics types
export type ShareRecentActivity = {
  event_type: 'VIEW' | 'DOWNLOAD' | string;
  asset_id?: string | null;
  download_key?: string | null;
  ip_address?: string | null;
  accessed_at: string; // ISO date string
};

export type ShareDailyView = {
  date: string; // YYYY-MM-DD
  count: number;
};

export type ShareAnalytics = {
  share_id: string;
  total_access_count: number;
  last_accessed_at?: string;
  events_by_type: Record<string, number>; // e.g., { "VIEW": 7, "DOWNLOAD": 1 }
  unique_visitors: number;
  daily_views: ShareDailyView[];
  downloads_by_asset: Record<string, number>; // asset_id -> download count
  downloads_by_key: Record<string, number>; // download_key -> count (e.g., { "480p-video": 1 })
  downloads_by_asset_key: Record<string, Record<string, number>>; // asset_id -> { download_key -> count }
  recent_activity: ShareRecentActivity[];
};

export type ShareAnalyticsResponse = {
  status: 'OK' | 'ERROR';
  data: ShareAnalytics;
};

// Template types
export type ShareTemplate = {
  template_id: string;
  name: string;
  description?: string;
  branding_config?: ShareBranding;
  download_options?: ShareDownloadOptions;
  preview_options?: SharePreviewOptions;
  default_expiry_days?: number;
  allow_feedback?: boolean;
  allow_approval?: boolean;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type ShareTemplatesResponse = {
  status: 'OK' | 'ERROR';
  data: {
    templates: ShareTemplate[];
    total?: number;
  };
};

export type ShareTemplateResponse = {
  status: 'OK' | 'ERROR';
  data: ShareTemplate;
};

// Template request types
export type CreateTemplateRequest = {
  name: string;
  description?: string;
  branding?: ShareBranding;
  download_options?: ShareDownloadOptions;
  preview_options?: SharePreviewOptions;
  default_expiry_days?: number;
  allow_feedback?: boolean;
  allow_approval?: boolean;
};

export type UpdateTemplateRequest = Partial<CreateTemplateRequest>;

// Preset template types
// NOTE: the backend dropped the review/delivery/special category grouping; the
// catalog is now a flat list of purpose presets. `category` is kept optional
// only for backwards-compat with any cached response and is no longer used.
export type PresetTemplateCategory = 'review' | 'delivery' | 'special';

export type PresetTemplateSummary = {
  preset_id: string;
  name: string;
  description: string;
  category?: PresetTemplateCategory;
  features: string[];
  default_expiry_days: number;
  // Addon required for this preset to be usable (e.g. "PRINT_EXPORT"). When set,
  // the UI gates/disables the corresponding purpose card unless the addon is on.
  requires_addon?: string;
};

export type PresetListResponse = {
  status: 'OK' | 'ERROR';
  data: {
    presets: PresetTemplateSummary[];
  };
};

export type AddPresetsRequest = {
  preset_ids: string[];
};

export type AddPresetsResponse = {
  status: 'OK' | 'ERROR';
  data: {
    created: number;
    templates: Array<{ template_id: string; name: string }>;
    errors?: string[];
  };
};

